/**
 * ContactNamespace.js
 * -------------------------------
 * Enhanced with dynamic modal configuration support
 */

import ErrorHandler from '../../utils/ErrorHandler.js';
import ContactAnalytics from '../utils/ContactAnalytics.js';
import ContactConfig from '../utils/ContactConfig.js';
import ContactTrigger from '../contactTrigger.js';
import ContactModal from './ContactModal.js';

const GLOBAL_KEY = '__CONTACT_NAMESPACE_SINGLETON__';
let _instance = null;

class ContactNamespace {
  constructor() {
    // Enforce global singleton
    if (typeof window !== 'undefined' && window[GLOBAL_KEY]) {
      // Singleton already exists - return existing instance silently
      return window[GLOBAL_KEY];
    }

    this.analytics = ContactAnalytics;
    this.config = ContactConfig;
    this.modal = null;
    this.trigger = null;
    this.clickHandler = null; // Add reference to click handler
    this.initialized = false;
    this.initializationPromise = null;
    this.errorHandler = new ErrorHandler({ module: 'ContactNamespace', maxErrors: 25 });

    if (typeof window !== 'undefined') {
      window[GLOBAL_KEY] = this;
      console.log('🌍 Stored ContactNamespace singleton on window');
    }

    _instance = this;
  }

  static getInstance() {
    if (_instance) return _instance;
    return new ContactNamespace();
  }

  static init() {
    return ContactNamespace.getInstance().init();
  }

  init() {
    if (this.initialized) {
      // Already initialized - return resolved promise silently
      return Promise.resolve(this);
    }
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this._performInitialization();
    return this.initializationPromise;
  }

  async _performInitialization() {
    try {
      console.log('🚀 Initializing ContactNamespace...');
      
      // FIXED: Pass 'this' (the namespace) to the ContactTrigger constructor
      // This ensures the ContactTriggerClient receives the required namespace parameter
      this.trigger = new ContactTrigger(this);
      
      // Store reference to click handler for modal configuration
      if (this.trigger && this.trigger.clickHandler) {
        this.clickHandler = this.trigger.clickHandler;
      }
      
      // Modal will be lazy-initialized on open
      this.modal = null;

      this.initialized = true;
      this.errorHandler.logSuccess('ContactNamespace initialized', { category: 'initialization' });
      return this;
    } catch (err) {
      this.errorHandler.handleFatalError('Initialization failed', err, { category: 'initialization' });
      this.initializationPromise = null;
      throw err;
    }
  }

  openModal() {
    try {
      console.log('🔍 DEBUG: Opening modal, checking for pending config...');
      console.log('🔍 DEBUG: clickHandler exists:', !!this.clickHandler);
      console.log('🔍 DEBUG: pendingModalConfig:', this.clickHandler?.getPendingModalConfig?.());
      
      if (!this.initialized) throw new Error('Not initialized');

      if (!this.modal) {
        console.log('🔄 Lazy initializing ContactModal');
        this.modal = new ContactModal(this);
        if (!this.modal.isInitialized()) {
          this.errorHandler.handleError('Modal DOM missing', new Error('Initialization error'), { category: 'modal-operation' });
          this._handleFallback();
          return false;
        }
      }

      // Apply pending modal configuration if available
      if (this.clickHandler?.getPendingModalConfig) {
        const pendingConfig = this.clickHandler.getPendingModalConfig();
        console.log('🔍 DEBUG: Retrieved pending config:', pendingConfig);

        if (pendingConfig) {
          const { modalType, triggerData } = pendingConfig;
          console.log('🔍 DEBUG: Applying pending modal config:', { modalType, triggerData });

          this.modal.updateModalConfiguration(modalType, triggerData);

          // Clear the pending config
          this.clickHandler.clearPendingModalConfig();
          console.log('🔍 DEBUG: Cleared pending config');
        }
      }

      // Always set the buttonId hidden field so plain contact buttons are tracked too
      const lastButtonId = this.clickHandler?.lastClickedButtonId;
      if (lastButtonId !== undefined && this.modal?.form) {
        let btnField = document.getElementById('contact-button-id');
        if (!btnField) {
          btnField = document.createElement('input');
          btnField.type = 'hidden';
          btnField.id = 'contact-button-id';
          btnField.name = 'buttonId';
          this.modal.form.appendChild(btnField);
        }
        btnField.value = lastButtonId;
      }

      const opened = this.modal.open();
      if (opened) {
        this.errorHandler.logSuccess('Modal opened', { 
          category: 'modal-operation',
          modalType: this.modal.getCurrentModalType?.()
        });
      }
      return opened;
    } catch (err) {
      this.errorHandler.handleFatalError('Error opening modal', err, { category: 'modal-operation' });
      this._handleFallback();
      return false;
    }
  }

  trackClick(button) {
    try {
      return this.analytics.trackContactClick(button, { pageUrl: window.location.href });
    } catch (err) {
      this.errorHandler.handleError('Analytics tracking failed', err, { category: 'analytics' });
    }
  }

  applyConfig(config) {
    try {
      return this.config.applyModalConfig(config, this.modal?.modal);
    } catch (err) {
      this.errorHandler.handleError('Applying config failed', err, { category: 'configuration' });
    }
  }

  getButtonConfig(button) {
    try {
      return this.config.getButtonConfig(button);
    } catch (err) {
      this.errorHandler.handleError('Button config extraction failed', err, { category: 'configuration' });
      return null;
    }
  }

  _handleFallback() {
    if (typeof window !== 'undefined') {
      console.log('🔄 Redirecting to /contact fallback');
      window.location.href = '/contact';
    }
  }

  reinitializeModal() {
    if (this.modal) this.modal.destroy();
    this.modal = new ContactModal(this);
    return this.modal.isInitialized();
  }

  // ADDED: Method that ContactTriggerClient expects to exist
  reinitializeModalSync() {
    try {
      return this.reinitializeModal();
    } catch (err) {
      this.errorHandler.handleError('Modal sync reinitialize failed', err, { category: 'modal-operation' });
      return false;
    }
  }

  healthCheck() {
    try {
      const baseHealth = {
        initialized: this.initialized,
        components: {
          analytics: !!this.analytics,
          config: !!this.config,
          trigger: !!this.trigger,
          modal: !!this.modal && this.modal.isInitialized(),
          clickHandler: !!this.clickHandler
        },
        modalReady: this.modal ? this.modal.isInitialized() : false,
        errors: this.errorHandler.getErrorStats(),
        timestamp: new Date().toISOString()
      };

      // Add modal configuration status
      if (this.modal && this.modal.getCurrentModalType) {
        baseHealth.modalConfig = {
          currentType: this.modal.getCurrentModalType(),
          availableConfigs: Object.keys(this.modal.getModalConfigs?.() || {}),
          hasValidationRules: !!this.modal.getValidationRules
        };
      }

      // Add pending modal configuration status
      if (this.clickHandler && this.clickHandler.getPendingModalConfig) {
        const pendingConfig = this.clickHandler.getPendingModalConfig();
        baseHealth.pendingModalConfig = {
          hasPending: !!pendingConfig,
          pendingType: pendingConfig?.modalType || null
        };
      }

      return baseHealth;
      
    } catch (error) {
      this.errorHandler.handleError('Health check failed', error, {
        category: 'health-check'
      });
      
      return {
        initialized: false,
        components: {},
        modalReady: false,
        errors: [{ message: error.message, timestamp: new Date().toISOString() }],
        timestamp: new Date().toISOString()
      };
    }
  }

  getRecentErrors(minutes = 5) {
    return this.errorHandler.getRecentErrors(minutes);
  }

  hasCriticalIssues() {
    return this.errorHandler.hasFatalErrors();
  }

  destroy() {
    try {
      this.modal?.destroy();
      this.trigger?.destroy?.();
      this.clickHandler = null;
      this.initialized = false;
      if (typeof window !== 'undefined' && window[GLOBAL_KEY] === this) {
        delete window[GLOBAL_KEY];
      }
      _instance = null;
      this.errorHandler.logSuccess('ContactNamespace destroyed', { category: 'lifecycle' });
    } catch (err) {
      this.errorHandler.handleError('Destroy failed', err, { category: 'lifecycle' });
    }
  }
}

export default ContactNamespace;